import React, { useState } from 'react';
import {
  X, Building2, Globe, FileText, Hash, MapPin, AlertCircle, Loader2, ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/features/auth';
import { companyService } from '@/lib/companyService';
import { countries } from '@/lib/countries';
import { getCountryProfile } from '@/lib/countryProfiles';
import { emitToast } from '@/components/ui/Toast';

/* ─────────────────────────────────────────────────────────────────────────────
   Props
───────────────────────────────────────────────────────────────────────────── */
interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newCompanyId: string) => void;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Static jurisdiction maps  (mirrors OnboardingPage / countryProfiles)
───────────────────────────────────────────────────────────────────────────── */
const CANADIAN_PROVINCES = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
  'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia',
  'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon',
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

/** Returns the jurisdiction list for countries that need one, or null. */
function getJurisdictions(countryCode: string): string[] | null {
  if (countryCode === 'CA') return CANADIAN_PROVINCES;
  if (countryCode === 'US') return US_STATES;
  const found = countries.find((c) => c.code === countryCode);
  return found?.jurisdictions ?? null;
}

/** Label for the jurisdiction field depending on country */
function jurisdictionLabel(countryCode: string): string {
  if (countryCode === 'CA') return 'Province of Incorporation';
  if (countryCode === 'US') return 'State of Incorporation';
  return 'Jurisdiction / Region';
}

/* ─────────────────────────────────────────────────────────────────────────────
   Form state shape
───────────────────────────────────────────────────────────────────────────── */
interface FormState {
  country: string;
  legal_name: string;
  registration_number: string;
  tax_id: string;
  jurisdiction: string;
  /** Keyed by localField.key, e.g. 'deductions_portfolio', 'cra_program_account' */
  localFields: Record<string, string>;
}

const EMPTY: FormState = {
  country: '',
  legal_name: '',
  registration_number: '',
  tax_id: '',
  jurisdiction: '',
  localFields: {},
};

/* ─────────────────────────────────────────────────────────────────────────────
   Reusable styled field wrapper
───────────────────────────────────────────────────────────────────────────── */
function FieldLabel({
  icon, label, required,
}: { icon: React.ReactNode; label: string; required?: boolean }) {
  return (
    <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
      <span className="text-gray-400">{icon}</span>
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
  );
}

interface FieldInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
}
function FieldInput({ error, className = '', ...rest }: FieldInputProps) {
  return (
    <div>
      <input
        {...rest}
        className={`w-full px-4 py-2.5 border rounded-lg text-sm text-gray-900 placeholder:text-gray-400
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors
          disabled:bg-gray-100 disabled:cursor-not-allowed
          ${error ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}
          ${className}`}
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

interface FieldSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: string;
  children: React.ReactNode;
}
function FieldSelect({ error, children, className = '', ...rest }: FieldSelectProps) {
  return (
    <div>
      <select
        {...rest}
        className={`w-full px-4 py-2.5 border rounded-lg text-sm text-gray-900
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
          transition-colors appearance-none bg-white
          disabled:bg-gray-100 disabled:cursor-not-allowed
          ${error ? 'border-red-400 bg-red-50' : 'border-gray-300'}
          ${className}`}
      >
        {children}
      </select>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────────── */
export function AddCompanyModal({ isOpen, onClose, onSuccess }: AddCompanyModalProps) {
  const { user } = useAuth();

  const [form, setForm]           = useState<FormState>(EMPTY);
  const [errors, setErrors]       = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError]   = useState<string | null>(null);
  /** Set when checkCompanyExists returns an owner email (duplicate guard). */
  const [duplicateOwner, setDuplicateOwner] = useState<string | null>(null);

  if (!isOpen) return null;

  /* ── Helpers ── */
  const countrySelected = form.country !== '';
  const profile = countrySelected ? getCountryProfile(form.country) : null;
  const jurisdictions = countrySelected ? getJurisdictions(form.country) : null;
  const needsJurisdiction = jurisdictions !== null && jurisdictions.length > 0;

  const setField = (key: keyof Omit<FormState, 'localFields'>, value: string) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => { const n = { ...p }; delete n[key]; return n; });
  };

  const setLocalField = (key: string, value: string) => {
    setForm((p) => ({ ...p, localFields: { ...p.localFields, [key]: value } }));
    setErrors((p) => { const n = { ...p }; delete n[`local_${key}`]; return n; });
  };

  /** When country changes, reset everything downstream */
  const handleCountryChange = (code: string) => {
    setForm({ ...EMPTY, country: code });
    setErrors({});
    setApiError(null);
    setDuplicateOwner(null);
  };

  /** When tax_id changes, clear any previous duplicate warning */
  const handleTaxIdChange = (value: string) => {
    setField('tax_id', value);
    setDuplicateOwner(null);
  };

  /* ── Validation ── */
  const validate = (): boolean => {
    const e: Record<string, string> = {};

    if (!form.country)                      e.country = 'יש לבחור מדינה';
    if (!form.legal_name.trim())            e.legal_name = 'שם החברה הוא שדה חובה';
    if (!form.registration_number.trim())   e.registration_number = 'שדה חובה';
    if (!form.tax_id.trim())                e.tax_id = 'שדה חובה';
    if (needsJurisdiction && !form.jurisdiction) {
      e.jurisdiction = 'שדה חובה';
    }

    // Validate required local fields
    profile?.localFields?.forEach((lf) => {
      if (lf.required && !form.localFields[lf.key]?.trim()) {
        e[`local_${lf.key}`] = `${lf.label} הוא שדה חובה`;
      }
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!user?.id) return;

    setSubmitting(true);
    setApiError(null);
    setDuplicateOwner(null);

    // ── Duplicate guard: call check_company_exists RPC first ────────────
    const { ownerEmail, error: checkError } = await companyService.checkCompanyExists(
      form.tax_id,
      form.country
    );

    if (checkError) {
      // RPC call itself failed — surface generic error, still allow submit
      console.warn('[AddCompanyModal] duplicate-check RPC failed:', checkError);
    }

    if (ownerEmail) {
      // Company already registered — stop here, show owner email
      setDuplicateOwner(ownerEmail);
      setSubmitting(false);
      return;
    }

    // Build local_requirements from optional local fields
    const localReqs: Record<string, string> = {};
    profile?.localFields?.forEach((lf) => {
      const val = form.localFields[lf.key]?.trim();
      if (val) localReqs[lf.key] = val;
    });

    try {
      const { data, error } = await companyService.createCompany(user.id, {
        legal_name:          form.legal_name.trim(),
        country:             form.country,
        jurisdiction:        form.jurisdiction.trim() || null,
        registration_number: form.registration_number.trim(),
        tax_id:              form.tax_id.trim(),
        lei:                 null,
        duns:                null,
        local_requirements:  Object.keys(localReqs).length > 0 ? localReqs : null,
      });

      if (error || !data?.company) {
        // Extract the most useful message from the Supabase error object
        const msg = (() => {
          if (!error) return 'שגיאה ביצירת החברה. אנא נסה שוב.';
          const err = error as Record<string, unknown>;
          // 42501 = RLS / permission denied
          if (err.code === '42501' || err.code === 'PGRST301') {
            return 'אין הרשאות מתאימות. ודא שאתה מחובר ונסה שוב (קוד: 403).';
          }
          // 23505 = unique_violation (country + tax_id DB constraint)
          if (err.code === '23505') {
            return 'חברה עם מספר זהה כבר קיימת במערכת. אנא פנה למנהל החברה.';
          }
          if (typeof err.message === 'string' && err.message) return err.message;
          if (typeof err.error_description === 'string') return err.error_description;
          return 'שגיאה ביצירת החברה. אנא נסה שוב.';
        })();
        setApiError(msg);
        return;
      }

      emitToast(`החברה "${form.legal_name}" נוצרה בהצלחה!`, 'success');
      setForm(EMPTY);
      setErrors({});
      onSuccess(data.company.id);
    } catch (err) {
      console.error('AddCompanyModal unexpected error:', err);
      setApiError('שגיאה בלתי צפויה. אנא נסה שוב.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setForm(EMPTY);
    setErrors({});
    setApiError(null);
    setDuplicateOwner(null);
    onClose();
  };

  /* ── Render ── */
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">

        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

        {/* Panel */}
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col">

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">הוסף חברה חדשה</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  בחר מדינה תחילה — השדות יתעדכנו בהתאם
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg
                transition-colors disabled:opacity-40"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

              {/* Duplicate-company warning banner */}
              {duplicateOwner && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-300
                  text-amber-800 text-sm px-4 py-3 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                  <span>
                    החברה כבר מוקמת במערכת. המשתמש הראשי הוא:{' '}
                    <strong className="font-semibold">{duplicateOwner}</strong>
                    {' '}— ליצירת קשר.
                  </span>
                </div>
              )}

              {/* Generic API error banner */}
              {apiError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200
                  text-red-700 text-sm px-4 py-3 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{apiError}</span>
                </div>
              )}

              {/* ── STEP 1: Country (always visible) ── */}
              <div className="space-y-1.5">
                <FieldLabel icon={<Globe className="w-3.5 h-3.5" />} label="מדינה" required />
                <FieldSelect
                  value={form.country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  error={errors.country}
                >
                  <option value="">בחר מדינה...</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </FieldSelect>
                {!countrySelected && (
                  <p className="flex items-center gap-1 text-xs text-gray-400">
                    <AlertCircle className="w-3 h-3" />
                    בחר מדינה כדי להמשיך
                  </p>
                )}
              </div>

              {/* ── STEP 2: Country-specific fields (revealed after country selection) ── */}
              {countrySelected && profile && (
                <>
                  {/* Divider */}
                  <div className="flex items-center gap-2 pt-1">
                    <div className="flex-1 h-px bg-gray-100" />
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-primary-600
                      uppercase tracking-wider">
                      <ChevronRight className="w-3.5 h-3.5" />
                      פרטי הרישום
                    </span>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>

                  {/* Company (Legal) Name */}
                  <div className="space-y-1.5">
                    <FieldLabel
                      icon={<Building2 className="w-3.5 h-3.5" />}
                      label="שם החברה (שם משפטי)"
                      required
                    />
                    <FieldInput
                      type="text"
                      value={form.legal_name}
                      onChange={(e) => setField('legal_name', e.target.value)}
                      placeholder="לדוגמה: Axelio Ltd"
                      dir="auto"
                      error={errors.legal_name}
                      disabled={submitting}
                    />
                  </div>

                  {/* Jurisdiction (Province / State) — only for CA / US + others with jurisdictions */}
                  {needsJurisdiction && (
                    <div className="space-y-1.5">
                      <FieldLabel
                        icon={<MapPin className="w-3.5 h-3.5" />}
                        label={jurisdictionLabel(form.country)}
                        required
                      />
                      <FieldSelect
                        value={form.jurisdiction}
                        onChange={(e) => setField('jurisdiction', e.target.value)}
                        error={errors.jurisdiction}
                        disabled={submitting}
                      >
                        <option value="">בחר...</option>
                        {jurisdictions!.map((j) => (
                          <option key={j} value={j}>{j}</option>
                        ))}
                      </FieldSelect>
                    </div>
                  )}

                  {/* Registration Number — label from country profile */}
                  <div className="space-y-1.5">
                    <FieldLabel
                      icon={<FileText className="w-3.5 h-3.5" />}
                      label={profile.registrationNumberLabel}
                      required
                    />
                    <FieldInput
                      type="text"
                      value={form.registration_number}
                      onChange={(e) => setField('registration_number', e.target.value)}
                      placeholder={profile.registrationNumberPlaceholder}
                      dir="ltr"
                      error={errors.registration_number}
                      disabled={submitting}
                    />
                    {profile.registrationNumberHint && !errors.registration_number && (
                      <p className="text-xs text-gray-400">{profile.registrationNumberHint}</p>
                    )}
                  </div>

                  {/* Tax ID — label from country profile */}
                  <div className="space-y-1.5">
                    <FieldLabel
                      icon={<Hash className="w-3.5 h-3.5" />}
                      label={profile.taxIdLabel}
                      required
                    />
                    <FieldInput
                      type="text"
                      value={form.tax_id}
                      onChange={(e) => handleTaxIdChange(e.target.value)}
                      placeholder={profile.taxIdPlaceholder}
                      dir="ltr"
                      error={errors.tax_id}
                      disabled={submitting}
                    />
                    {profile.taxIdHint && !errors.tax_id && (
                      <p className="text-xs text-gray-400">{profile.taxIdHint}</p>
                    )}
                  </div>

                  {/* Optional country-specific local fields */}
                  {profile.localFields?.map((lf) => (
                    <div key={lf.key} className="space-y-1.5">
                      <FieldLabel
                        icon={<FileText className="w-3.5 h-3.5" />}
                        label={lf.label}
                        required={lf.required}
                      />
                      <FieldInput
                        type="text"
                        value={form.localFields[lf.key] ?? ''}
                        onChange={(e) => setLocalField(lf.key, e.target.value)}
                        placeholder={lf.placeholder}
                        dir="ltr"
                        error={errors[`local_${lf.key}`]}
                        disabled={submitting}
                      />
                      {lf.hint && !errors[`local_${lf.key}`] && (
                        <p className="text-xs text-gray-400">{lf.hint}</p>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="px-6 pb-6 pt-3 flex items-center justify-end gap-3 border-t border-gray-100">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300
                  rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
              >
                ביטול
              </button>
              <button
                type="submit"
                disabled={submitting || !countrySelected}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white
                  bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    יוצר חברה...
                  </>
                ) : (
                  <>
                    <Building2 className="w-4 h-4" />
                    צור חברה
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
